import React from 'react';

import { useForm } from 'react-hook-form';

import { Box, Button, Divider, Grid, MenuItem, Stack, TextField } from '@mui/material';

import { getRegions } from '@luxuryescapes/lib-regions';

import { brands } from '../../consts/brands';

export type AddMockOfferMetricsFormData = {
  offerId: string;
  region: string;
  mpv: number;
  date: string;
  brand: string;
  totalViews7d: number;
  conversionRate7d: number;
  mpvNoPromo7d: number;
  conversionNoPromo7d: number;
  conversionPromo7d: number;
  totalOrders7d: number;
  totalMargin7d: number;
  totalSales7d: number;
  totalOrdersNoPromo7d: number;
  totalOrdersPromo7d: number;
  marginNoPromo7d: number;
  marginDiff7d: number;
};

type AddMockOfferMetricsFormProps = {
  onSubmit: (data: AddMockOfferMetricsFormData) => void;
  isLoading?: boolean;
  defaultValues?: Partial<AddMockOfferMetricsFormData>;
};

const defaultFormValues: AddMockOfferMetricsFormData = {
  offerId: '',
  region: 'AU',
  mpv: 0,
  date: '',
  brand: 'luxuryescapes',
  totalViews7d: 0,
  conversionRate7d: 0,
  mpvNoPromo7d: 0,
  conversionNoPromo7d: 0,
  conversionPromo7d: 0,
  totalOrders7d: 0,
  totalMargin7d: 0,
  totalSales7d: 0,
  totalOrdersNoPromo7d: 0,
  totalOrdersPromo7d: 0,
  marginNoPromo7d: 0,
  marginDiff7d: 0,
};

export default function AddMockOfferMetricsForm({
  onSubmit,
  isLoading = false,
  defaultValues = {},
}: AddMockOfferMetricsFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddMockOfferMetricsFormData>({
    defaultValues: { ...defaultFormValues, ...defaultValues },
    mode: 'onBlur',
  });

  const regionOptions = getRegions().map((r) => ({
    label: r.name,
    value: r.code,
  }));

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2} justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          <Stack spacing={2}>
            <TextField
              {...register('offerId', { required: 'Offer ID is required' })}
              label="Offer ID"
              variant="outlined"
              fullWidth
              error={!!errors.offerId}
              helperText={errors.offerId?.message}
            />
            <TextField
              {...register('region', { required: 'Region is required' })}
              label="Region"
              variant="outlined"
              defaultValue="AU"
              fullWidth
              select
              error={!!errors.region}
              helperText={errors.region?.message}
            >
              {regionOptions.map((region) => (
                <MenuItem key={region.value} value={region.value}>
                  {region.label} ({region.value})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              {...register('brand', { required: 'Brand is required' })}
              label="Brand"
              variant="outlined"
              fullWidth
              select
              error={!!errors.brand}
              helperText={errors.brand?.message}
            >
              {brands.map((brand) => (
                <MenuItem key={brand.value} value={brand.value}>
                  {brand.title}
                </MenuItem>
              ))}
            </TextField>
            <Divider />
            <TextField
              {...register('mpv', {
                valueAsNumber: true,
                required: 'MPV is required',
                min: { value: 0, message: 'MPV must be greater than or equal to 0' },
              })}
              label="MPV (Minimum Purchase Value)"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.mpv}
              helperText={errors.mpv?.message}
            />
            <TextField
              {...register('totalViews7d', {
                valueAsNumber: true,
                required: 'Total Views 7d is required',
                min: { value: 0, message: 'Total Views 7d must be greater than or equal to 0' },
              })}
              label="Total Views 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.totalViews7d}
              helperText={errors.totalViews7d?.message}
            />
            <TextField
              {...register('conversionRate7d', {
                valueAsNumber: true,
                required: 'Conversion Rate 7d is required',
                min: { value: 0, message: 'Conversion Rate 7d must be greater than or equal to 0' },
              })}
              label="Conversion Rate 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.conversionRate7d}
              helperText={errors.conversionRate7d?.message}
            />
            <TextField
              {...register('mpvNoPromo7d', {
                valueAsNumber: true,
                required: 'MPV No Promo 7d is required',
                min: { value: 0, message: 'MPV No Promo 7d must be greater than or equal to 0' },
              })}
              label="MPV No Promo 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.mpvNoPromo7d}
              helperText={errors.mpvNoPromo7d?.message}
            />
            <TextField
              {...register('conversionNoPromo7d', {
                valueAsNumber: true,
                required: 'Conversion No Promo 7d is required',
                min: { value: 0, message: 'Conversion No Promo 7d must be greater than or equal to 0' },
              })}
              label="Conversion No Promo 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.conversionNoPromo7d}
              helperText={errors.conversionNoPromo7d?.message}
            />
            <TextField
              {...register('conversionPromo7d', {
                valueAsNumber: true,
                required: 'Conversion Promo 7d is required',
                min: { value: 0, message: 'Conversion Promo 7d must be greater than or equal to 0' },
              })}
              label="Conversion Promo 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.conversionPromo7d}
              helperText={errors.conversionPromo7d?.message}
            />
            <TextField
              {...register('totalOrders7d', {
                valueAsNumber: true,
                required: 'Total Orders 7d is required',
                min: { value: 0, message: 'Total Orders 7d must be greater than or equal to 0' },
              })}
              label="Total Orders 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.totalOrders7d}
              helperText={errors.totalOrders7d?.message}
            />
            <TextField
              {...register('totalMargin7d', {
                valueAsNumber: true,
                required: 'Total Margin 7d is required',
                min: { value: 0, message: 'Total Margin 7d must be greater than or equal to 0' },
              })}
              label="Total Margin 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.totalMargin7d}
              helperText={errors.totalMargin7d?.message}
            />
            <TextField
              {...register('totalSales7d', {
                valueAsNumber: true,
                required: 'Total Sales 7d is required',
                min: { value: 0, message: 'Total Sales 7d must be greater than or equal to 0' },
              })}
              label="Total Sales 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.totalSales7d}
              helperText={errors.totalSales7d?.message}
            />
            <TextField
              {...register('totalOrdersNoPromo7d', {
                valueAsNumber: true,
                required: 'Total Orders No Promo 7d is required',
                min: { value: 0, message: 'Total Orders No Promo 7d must be greater than or equal to 0' },
              })}
              label="Total Orders No Promo 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.totalOrdersNoPromo7d}
              helperText={errors.totalOrdersNoPromo7d?.message}
            />
            <TextField
              {...register('totalOrdersPromo7d', {
                valueAsNumber: true,
                required: 'Total Orders Promo 7d is required',
                min: { value: 0, message: 'Total Orders Promo 7d must be greater than or equal to 0' },
              })}
              label="Total Orders Promo 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.totalOrdersPromo7d}
              helperText={errors.totalOrdersPromo7d?.message}
            />
            <TextField
              {...register('marginNoPromo7d', {
                valueAsNumber: true,
                required: 'Margin No Promo 7d is required',
                min: { value: 0, message: 'Margin No Promo 7d must be greater than or equal to 0' },
              })}
              label="Margin No Promo 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.marginNoPromo7d}
              helperText={errors.marginNoPromo7d?.message}
            />
            <TextField
              {...register('marginDiff7d', {
                valueAsNumber: true,
                required: 'Margin Diff 7d is required',
                min: { value: 0, message: 'Margin Diff 7d must be greater than or equal to 0' },
              })}
              label="Margin Diff 7d"
              variant="outlined"
              type="number"
              fullWidth
              error={!!errors.marginDiff7d}
              helperText={errors.marginDiff7d?.message}
            />
            <Button type="submit" variant="contained" color="primary" disabled={isLoading} fullWidth>
              {isLoading ? 'Adding Mock Data...' : 'Add Mock Offer Metrics Data (test only)'}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
}
